// Canonical install URLs for the public native apps. Lifted into shared/ so
// both the client (in-app install affordances) and the server (welcome email
// + checkout-redirect + web manifest) consume the same constants without
// copy-pasting.
//
// **Per-env behavior.** The Android package id differs between environments:
// `app.mhphysique.fitness` is the prod package (the public Play listing); the
// dev environment ships `app.mhphysique.fitness.dev` to its own internal-
// testing Play listing (reachable to tester accounts only). Each env should
// recommend installing — and detect via `getInstalledRelatedApps()` — its
// matching package; otherwise the install sheet on `dev.mh-physique.app`
// can't validate the installed-app collapse path without sideloading the
// prod APK. Canary is treated as prod (same APK, public listing).
//
// **Sources of truth per side.**
// - Server: `config().googleplay.packagename` (loaded from
//   `server/functions/env-config/.env.<env>`) is the canonical env-bound
//   package id. Server consumers prefer that over the helpers here.
// - Client: `AppEnvironment.name` (`'prod'` | `'dev'` | `'local'`) keys the
//   `androidPackageIdForEnv` helper below.
// - iOS App Store id is environment-agnostic — there's no dev App Store
//   listing; sandbox/TestFlight installs use the same id.

export const ANDROID_PACKAGE_ID_PROD = 'app.mhphysique.fitness';
export const ANDROID_PACKAGE_ID_DEV = 'app.mhphysique.fitness.dev';

export const IOS_APP_STORE_ID = '6767633069';

// locale-agnostic — the App Store resolves to the user's region without a
// locale prefix
export const IOS_APP_STORE_URL = `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;

// `itms-apps://` variant that opens the native App Store app DIRECTLY,
// skipping the web listing. Opening the https URL above through an in-app
// Safari view (e.g. @capacitor/browser) first renders apps.apple.com as a
// web page and only then hands off to the App Store — a visible flicker.
// iOS routes the itms-apps scheme straight to the store. Use this for a
// programmatic "open the store" handoff on iOS; keep the https URL for
// `<a href>` badges and any context that must also work on the web.
export const IOS_APP_STORE_ITMS_URL = `itms-apps://apps.apple.com/app/id${IOS_APP_STORE_ID}`;

// Bare Play Store listing URL (no install referrer). For the install-referrer
// variant used by the purchase-claim flow, see buildAndroidInstallUrl in
// server/functions/src/helpers/app-store-urls.ts.
export const ANDROID_PLAY_STORE_URL_PROD = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID_PROD}`;
export const ANDROID_PLAY_STORE_URL_DEV = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID_DEV}`;

// Default export kept for tests that assert "the canonical prod URL"; prefer
// `androidPlayStoreUrlForEnv` for any URL that ships to users.
export const ANDROID_PLAY_STORE_URL = ANDROID_PLAY_STORE_URL_PROD;

// Accept any string and treat anything other than `'dev'` as prod. `'local'`
// resolves to prod intentionally — the Angular `local` build is the web
// developer environment, not the Android `.local` flavor, and recommending a
// public package is the safer default if a developer ever clicks the badge.
// The `(string & {})` opt-out preserves the literal suggestions in IDEs
// without collapsing the union to plain `string` at the type level.
export type InstallUrlEnv = 'prod' | 'dev' | 'local' | (string & {});

// Returns the Android package id (`app.mhphysique.fitness` or its `.dev`
// counterpart) keyed on the bundle environment. Unrecognized env names fall
// through to the prod package id.
export function androidPackageIdForEnv(envName: InstallUrlEnv): string {
  return envName === 'dev' ? ANDROID_PACKAGE_ID_DEV : ANDROID_PACKAGE_ID_PROD;
}

// Returns the Play Store listing URL for the package id matching the bundle
// environment. Suitable for `<a href>` store badges and for the web manifest's
// `related_applications[].url` field.
export function androidPlayStoreUrlForEnv(envName: InstallUrlEnv): string {
  return envName === 'dev' ? ANDROID_PLAY_STORE_URL_DEV : ANDROID_PLAY_STORE_URL_PROD;
}

// #4625 — where the Settings "Rate the app" row sends somebody.
//
// **These are store deep links, NOT the native review prompt**, and that
// distinction is the entire reason the row is allowed to exist: the native
// prompt must never be attached to a button, because the operating system may
// decide to show nothing and a button that does nothing looks broken. A store
// link always works and is never subject to a quota. See
// design-docs/review-prompts.md.
//
// It converts poorly compared with the native prompt, and that is expected — it
// is the right home for somebody who CHOOSES to rate us.

// Apple's write-a-review deep link, in the `itms-apps://` form.
//
// **The scheme is what makes this work, not the parameter.** The
// `action=write-review` parameter only opens the review composer once the App
// Store APP handles the URL. Handed the https form, an in-app Safari view
// renders apps.apple.com as a web page instead — see the comment on
// IOS_APP_STORE_ITMS_URL above, which is the same trap in the same file. So
// this is for a programmatic handoff via `window.location.href`, exactly as
// `version-check.ios.ts` already does for "open the store".
export const IOS_WRITE_REVIEW_URL = `${IOS_APP_STORE_ITMS_URL}?action=write-review`;


// Play has no write-review deep link of its own — the listing IS where somebody
// rates an app — so this is the per-environment listing URL. **Per-environment
// matters here**: the dev flavour ships a different package with its own
// listing, and a build installed through Play internal sharing may well be that
// one, so a hardcoded production package would send a tester to a listing their
// build is not.
export function androidWriteReviewUrlForEnv(envName: InstallUrlEnv): string {
  return androidPlayStoreUrlForEnv(envName);
}
